import { Skeleton, Table } from 'antd'
import { useInfiniteQuery } from 'react-query'
import { CustomerJobItem, getCustomerJobs } from './jobsApi'
import { useState } from 'react'
import { SorterResult } from 'antd/es/table/interface'
import InfiniteScroll from 'react-infinite-scroll-component'
import dayjs from 'dayjs'

export type Props = {
  customerId: number
}

const CustomerJobs = ({ customerId }: Props) => {
  const PAGE_SIZE = 10
  const [page] = useState(1)
  const [sortColumn, setSortColumn] = useState('name')
  const [ascending, setAscending] = useState(true)
  const params = {
    customerId,
    page,
    pageSize: PAGE_SIZE,
    sortColumn,
    ascending
  }

  const infiniteJobsQuery = useInfiniteQuery(['customer-jobs-infinite', params],
    ({ pageParam = 1 }) => getCustomerJobs({ ...params, page: pageParam }),
    {
      getNextPageParam: (_, pages) => pages.length + 1
    }
  )

  const infJobs = infiniteJobsQuery.data?.pages.flatMap(x => x.rows) ?? []
  const total = infiniteJobsQuery.data?.pages[0].totalRows ?? 0

  return <InfiniteScroll
    height={400}
    dataLength={infJobs.length}
    hasMore={infJobs.length < total}
    next={() => infiniteJobsQuery.fetchNextPage()}
    loader={<Skeleton active paragraph={{ rows: 1 }} />}
  >
    <Table
      dataSource={infJobs}
      loading={infiniteJobsQuery.isLoading}
      size="small"
      pagination={false}
      rowKey="jobId"
      columns={[
        {
          title: 'No.',
          dataIndex: 'no',
          sorter: (a, b) => a.no.localeCompare(b.no)
        },
        {
          title: 'Name',
          dataIndex: 'name',
          sorter: (a, b) => a.name.localeCompare(b.name)
        },
        {
          title: 'Estimate Date',
          dataIndex: 'estimateDate',
          render: (estimateDate: string) => {
            return dayjs(estimateDate).format('MMM D, YYYY')
          },
          sorter: (a, b) => a.estimateDate.localeCompare(b.estimateDate)
        },
        {
          title: 'Type',
          dataIndex: 'type',
          render: (type) => {
            switch (type) {
              case 1: return 'Project'
              case 2: return 'Ticket'
              case 3: return 'Maintenance'
              default: return ''
            }
          },
          sorter: (a, b) => a.type - b.type
        },
        {
          title: 'Status',
          dataIndex: 'status',
          render: (status) => {
            switch (status) {
              case 0: return 'Unscheduled'
              case 1: return 'Scheduled'
              case 2: return 'Waiting on Parts'
              case 3: return 'Completed'
              default: return ''
            }
          },
          sorter: (a, b) => a.status - b.status
        },
        {
          title: 'Division',
          dataIndex: 'divisions',
          sorter: (a, b) => a.divisions.localeCompare(b.divisions)
        },
        {
          title: 'Sales Person',
          dataIndex: 'salesPerson',
          sorter: (a, b) => a.salesPerson.localeCompare(b.salesPerson)
        }
      ]}
      onChange={(pagination, _filters, sorter) => {
        const sort = sorter as SorterResult<CustomerJobItem>
        setSortColumn(sort.field as string)
        setAscending(sort.order === 'ascend')
      }}
      showSorterTooltip={false}
      onRow={(row) => ({
        onClick: () => { window.location.href = `#!/${row.url}` },
        style: { cursor: 'pointer' }
      })}
    />
  </InfiniteScroll>
}
export default CustomerJobs
